import React, { useState, useEffect } from 'react';
import api from '../services/api';

const AdminDocumentManager = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('list');
  const [uploading, setUploading] = useState(false);
  const [fileTypeFilter, setFileTypeFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Folder management state
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null); // null = root level
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [showMoveDocumentModal, setShowMoveDocumentModal] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [documentToMove, setDocumentToMove] = useState(null);
  const [draggedDocument, setDraggedDocument] = useState(null);
  const [folderForm, setFolderForm] = useState({ name: '', description: '' });

  // Upload mode: 'file' or 'embed'
  const [uploadMode, setUploadMode] = useState('file');

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'general',
    visibility: 'all',
    is_pinned: false,
    folder_id: null,
    file: null
  });

  // Embed form state
  const [embedForm, setEmbedForm] = useState({
    title: '',
    description: '',
    embed_url: '',
    embed_password: '',
    category: 'general',
    visibility: 'all',
    is_pinned: false,
    folder_id: null
  });

  useEffect(() => {
    fetchDocuments();
    fetchFolders();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/documents');

      if (response.data.success) {
        setDocuments(response.data.documents);
      } else {
        setError(response.data.message || 'Failed to load documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await api.get('/folders');
      if (response.data.success) {
        setFolders(response.data.folders);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
      setError('Failed to load folders');
    }
  };

  const createFolder = async () => {
    if (!folderForm.name.trim()) {
      setError('Folder name is required');
      return;
    }

    try {
      const response = await api.post('/folders', {
        name: folderForm.name.trim(),
        description: folderForm.description.trim()
      });

      if (response.data.success) {
        await fetchFolders();
        setShowCreateFolderModal(false);
        resetFolderForm();
        setError('');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      setError(error.response?.data?.message || 'Failed to create folder');
    }
  };

  const renameFolder = async () => {
    if (!folderToEdit || !folderForm.name.trim()) {
      setError('Folder name is required');
      return;
    }

    try {
      const response = await api.put(`/folders/${folderToEdit.id}`, {
        name: folderForm.name.trim(),
        description: folderForm.description.trim()
      });

      if (response.data.success) {
        await fetchFolders();
        setShowRenameFolderModal(false);
        setFolderToEdit(null);
        resetFolderForm();
        setError('');
      }
    } catch (error) {
      console.error('Error renaming folder:', error);
      setError(error.response?.data?.message || 'Failed to rename folder');
    }
  };

  const deleteFolder = async (folderId, folderName) => {
    if (!window.confirm(`Are you sure you want to delete the folder "${folderName}"? The folder must be empty.`)) {
      return;
    }

    try {
      const response = await api.delete(`/folders/${folderId}`);

      if (response.data.success) {
        await fetchFolders();
        if (selectedFolder === folderId) {
          setSelectedFolder(null);
        }
        setError('');
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      setError(error.response?.data?.message || 'Failed to delete folder');
    }
  };

  const moveDocument = async (documentId, folderId) => {
    try {
      const response = await api.put(`/documents/${documentId}/move`, {
        folder_id: folderId
      });

      if (response.data.success) {
        await fetchDocuments();
        setShowMoveDocumentModal(false);
        setDocumentToMove(null);
        setError('');
      }
    } catch (error) {
      console.error('Error moving document:', error);
      setError(error.response?.data?.message || 'Failed to move document');
    }
  };

  const resetFolderForm = () => {
    setFolderForm({ name: '', description: '' });
  };

  // Drag and drop handlers
  const handleDragStart = (e, document) => {
    setDraggedDocument(document);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', document.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetFolderId) => {
    e.preventDefault();

    if (!draggedDocument) return;

    // Don't move if already in target folder
    if (draggedDocument.folder_id === targetFolderId) {
      setDraggedDocument(null);
      return;
    }

    await moveDocument(draggedDocument.id, targetFolderId);
    setDraggedDocument(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      // Check file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'image/jpeg',
        'image/png',
        'image/gif'
      ];

      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Only PDF, Word, Excel, text, and image files are allowed.');
        return;
      }

      setUploadForm({
        ...uploadForm,
        file: file,
        title: uploadForm.title || file.name.split('.')[0] // Auto-fill title if empty
      });
      setError('');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      setError('Please select a file to upload');
      return;
    }

    if (!uploadForm.title.trim()) {
      setError('Document title is required');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title.trim());
      formData.append('description', uploadForm.description.trim());
      formData.append('category', uploadForm.category);
      formData.append('visibility', uploadForm.visibility);
      formData.append('is_pinned', uploadForm.is_pinned.toString());
      if (uploadForm.folder_id) {
        formData.append('folder_id', uploadForm.folder_id);
      }

      const response = await api.post('/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        resetUploadForm();
        setView('list');
        fetchDocuments();
      } else {
        setError(response.data.message || 'Failed to upload document');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setError('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await api.delete(`/documents/${documentId}`);

      if (response.data.success) {
        fetchDocuments();
      } else {
        setError(response.data.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document');
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const response = await api.get(`/documents/${documentId}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      setError('Failed to download document');
    }
  };

  const togglePin = async (documentId, currentPinStatus, title) => {
    try {
      const response = await api.put(`/documents/${documentId}`, {
        is_pinned: !currentPinStatus
      });

      if (response.data.success) {
        fetchDocuments();
      } else {
        setError(response.data.message || 'Failed to update document');
      }
    } catch (error) {
      console.error('Error updating document:', error);
      setError('Failed to update document');
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      title: '',
      description: '',
      category: 'general',
      visibility: 'all',
      is_pinned: false,
      folder_id: null,
      file: null
    });
    // Reset file input
    const fileInput = document.getElementById('file-upload');
    if (fileInput) fileInput.value = '';
  };

  const resetEmbedForm = () => {
    setEmbedForm({
      title: '',
      description: '',
      embed_url: '',
      embed_password: '',
      category: 'general',
      visibility: 'all',
      is_pinned: false,
      folder_id: null
    });
  };

  const handleEmbed = async (e) => {
    e.preventDefault();

    if (!embedForm.title.trim()) {
      setError('Document title is required');
      return;
    }
    if (!embedForm.embed_url.trim()) {
      setError('Document URL is required');
      return;
    }

    try {
      setUploading(true);

      const response = await api.post('/documents/embed', {
        title: embedForm.title.trim(),
        description: embedForm.description.trim(),
        embed_url: embedForm.embed_url.trim(),
        embed_password: embedForm.embed_password || null,
        category: embedForm.category,
        visibility: embedForm.visibility,
        is_pinned: embedForm.is_pinned,
        folder_id: embedForm.folder_id ? parseInt(embedForm.folder_id) : null
      });

      if (response.data.success) {
        resetEmbedForm();
        setView('list');
        fetchDocuments();
      } else {
        setError(response.data.message || 'Failed to link document');
      }
    } catch (error) {
      console.error('Error linking document:', error);
      setError(error.response?.data?.message || 'Failed to link document');
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (fileType) => {
    return '';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'policy': 'bg-red-100 text-red-800',
      'training': 'bg-blue-100 text-blue-800',
      'forms': 'bg-green-100 text-green-800',
      'resources': 'bg-purple-100 text-purple-800',
      'guidelines': 'bg-yellow-100 text-yellow-800',
      'general': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.general;
  };

  const getVisibilityColor = (visibility) => {
    const colors = {
      'all': 'bg-green-100 text-green-800',
      'admin_only': 'bg-red-100 text-red-800',
      'volunteers_only': 'bg-blue-100 text-blue-800'
    };
    return colors[visibility] || colors.all;
  };

  const getFileTypeCategory = (fileType) => {
    if (!fileType) return 'embedded';
    if (fileType.includes('pdf')) return 'pdfs';
    if (fileType.includes('word') || fileType.includes('text')) return 'docs';
    if (fileType.includes('excel') || fileType.includes('sheet')) return 'spreadsheets';
    if (fileType.includes('image')) return 'images';
    return 'other';
  };

  const filteredDocuments = documents.filter(doc => {
    // Folder filter
    if (selectedFolder === null) {
      // Root level - only show documents without folder
      if (doc.folder_id) return false;
    } else {
      // Specific folder - only show documents in that folder
      if (doc.folder_id !== selectedFolder) return false;
    }

    // File type filter
    if (fileTypeFilter !== 'all' && getFileTypeCategory(doc.file_type) !== fileTypeFilter) return false;

    // Search filter
    if (search) {
      const q = search.toLowerCase();
      const matchesSearch =
        doc.title.toLowerCase().includes(q) ||
        (doc.description || '').toLowerCase().includes(q);
      if (!matchesSearch) return false;
    }

    return true;
  });

  // Filter documents by selected folder first
  const documentsInCurrentFolder = documents.filter(doc => {
    if (selectedFolder === null) {
      return !doc.folder_id; // Root level - only docs without folder
    } else {
      return doc.folder_id === selectedFolder; // Specific folder
    }
  });

  const fileTypeCounts = {
    all: documentsInCurrentFolder.length,
    pdfs: documentsInCurrentFolder.filter(d => getFileTypeCategory(d.file_type) === 'pdfs').length,
    docs: documentsInCurrentFolder.filter(d => getFileTypeCategory(d.file_type) === 'docs').length,
    spreadsheets: documentsInCurrentFolder.filter(d => getFileTypeCategory(d.file_type) === 'spreadsheets').length,
    images: documentsInCurrentFolder.filter(d => getFileTypeCategory(d.file_type) === 'images').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
          <button 
            onClick={() => setError('')}
            className="float-right text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Document Management</h2>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {view === 'list' && (
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search documents..."
                className="pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-48"
              />
            </div>
          )}
          <button
            onClick={() => setView('list')}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              view === 'list'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            View
          </button>
          <button
            onClick={() => {
              resetUploadForm();
              resetEmbedForm();
              setView('upload');
            }}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              view === 'upload'
                ? 'bg-success-500 text-white'
                : 'bg-success-500 text-white hover:bg-success-600'
            }`}
          >
            Add Document
          </button>
        </div>
      </div>

      {/* Documents List View */}
      {view === 'list' && (
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* MOBILE: Folder Dropdown */}
          <div className="lg:hidden bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 mb-1">Current Folder</label>
                <select
                  value={selectedFolder || ''}
                  onChange={(e) => setSelectedFolder(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Documents</option>
                  {folders.map(folder => (
                    <option key={folder.id} value={folder.id}>
                      {folder.name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => setShowCreateFolderModal(true)}
                className="bg-primary-500 hover:bg-primary-600 text-white px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap"
              >
                + Folder
              </button>
            </div>
          </div>

          {/* DESKTOP: LEFT SIDEBAR - Folders Navigation */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow p-4 sticky top-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-900">Folders</h3>
                <button
                  onClick={() => setShowCreateFolderModal(true)}
                  className="text-blue-500 hover:text-blue-600 text-sm font-medium"
                  title="Create new folder"
                >
                  + New
                </button>
              </div>

              {/* Root level (All Documents) */}
              <button
                onClick={() => setSelectedFolder(null)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, null)}
                className={`w-full text-left px-3 py-2 rounded-md mb-1 transition-colors ${
                  selectedFolder === null
                    ? 'bg-blue-50 text-blue-700 font-medium'
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                All Documents
              </button>

              {/* Folders list */}
              <div className="space-y-1 mt-2">
                {folders.map(folder => (
                  <div
                    key={folder.id}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, folder.id)}
                    className={`group flex items-center justify-between px-3 py-2 rounded-md cursor-pointer transition-colors ${
                      selectedFolder === folder.id
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'hover:bg-gray-50 text-gray-700'
                    } ${draggedDocument && draggedDocument.folder_id !== folder.id ? 'border-2 border-dashed border-blue-300' : ''}`}
                  >
                    <div
                      onClick={() => setSelectedFolder(folder.id)}
                      className="flex-1"
                    >
                      {folder.name}
                    </div>

                    {/* Folder actions (visible on hover) */}
                    <div className="opacity-0 group-hover:opacity-100 flex space-x-1 ml-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderToEdit(folder);
                          setFolderForm({ name: folder.name, description: folder.description || '' });
                          setShowRenameFolderModal(true);
                        }}
                        className="text-gray-500 hover:text-blue-600 text-xs p-1"
                        title="Rename folder"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteFolder(folder.id, folder.name);
                        }}
                        className="text-gray-500 hover:text-red-600 text-xs p-1"
                        title="Delete folder"
                      >
                        Del
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* MAIN CONTENT AREA - Documents List */}
          <div className="flex-1 min-w-0">
          {/* File Type Filter */}
          {documents.length > 0 && (
            <div className="bg-white rounded-lg shadow p-3 sm:p-4 mb-4">
              <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-2 sm:mb-3">Filter by Type</h3>
              <div className="flex overflow-x-auto gap-2 pb-1 -mx-1 px-1">
                <button
                  onClick={() => setFileTypeFilter('all')}
                  className={`flex-shrink-0 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                    fileTypeFilter === 'all'
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All ({fileTypeCounts.all})
                </button>
                <button
                  onClick={() => setFileTypeFilter('pdfs')}
                  className={`flex-shrink-0 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                    fileTypeFilter === 'pdfs'
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  PDFs ({fileTypeCounts.pdfs})
                </button>
                <button
                  onClick={() => setFileTypeFilter('docs')}
                  className={`flex-shrink-0 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                    fileTypeFilter === 'docs'
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Docs ({fileTypeCounts.docs})
                </button>
                <button
                  onClick={() => setFileTypeFilter('spreadsheets')}
                  className={`flex-shrink-0 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                    fileTypeFilter === 'spreadsheets'
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Sheets ({fileTypeCounts.spreadsheets})
                </button>
                <button
                  onClick={() => setFileTypeFilter('images')}
                  className={`flex-shrink-0 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                    fileTypeFilter === 'images'
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Images ({fileTypeCounts.images})
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow">
            {documents.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No documents yet</h3>
                <p className="text-gray-600 mb-4">Upload your first document to get started</p>
                <button
                  onClick={() => setView('upload')}
                  className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Upload Document
                </button>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {search ? 'No documents match your search' : 'No documents in this category'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {search ? 'Try a different search term or file type.' : 'Try selecting a different file type'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredDocuments.map((doc) => (
                <div
                  key={doc.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, doc)}
                  className="p-4 sm:p-6 hover:bg-gray-50 cursor-move"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start space-x-3 sm:space-x-4 flex-1 min-w-0">
                      <div className="text-2xl sm:text-3xl flex-shrink-0">{getFileIcon(doc.file_type)}</div>

                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{doc.title}</h3>
                          {doc.is_pinned && (
                            <span className="text-yellow-500 text-xs sm:text-sm whitespace-nowrap">Pinned</span>
                          )}
                          <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getCategoryColor(doc.category)}`}>
                            {doc.category}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getVisibilityColor(doc.visibility)}`}>
                            {doc.visibility.replace('_', ' ')}
                          </span>
                          {doc.document_type === 'embedded' && (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                              doc.embed_url && doc.embed_url.includes('google.com')
                                ? 'bg-emerald-100 text-emerald-800'
                                : doc.embed_url && doc.embed_url.includes('canva.com')
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-indigo-100 text-indigo-800'
                            }`}>
                              {doc.embed_url && doc.embed_url.includes('google.com') ? 'Google'
                                : doc.embed_url && doc.embed_url.includes('canva.com') ? 'Canva'
                                : 'OneDrive'}
                            </span>
                          )}
                        </div>

                        <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                          {doc.description && (
                            <p className="mb-2 break-words line-clamp-3">{doc.description}</p>
                          )}
                          <div className="flex flex-wrap gap-x-3 gap-y-1">
                            {doc.document_type === 'embedded' ? (
                              <span className="whitespace-nowrap text-indigo-600">Linked Document</span>
                            ) : (
                              <>
                                <span className="whitespace-nowrap">{doc.file_name}</span>
                                <span className="whitespace-nowrap">{doc.file_size_mb} MB</span>
                              </>
                            )}
                            <span className="whitespace-nowrap">{new Date(doc.created_at).toLocaleDateString()}</span>
                            {doc.uploader_name && (
                              <span className="whitespace-nowrap">{doc.uploader_name} {doc.uploader_lastname}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-row sm:flex-col gap-2 sm:ml-4">
                      {doc.document_type === 'embedded' ? (
                        <a
                          href={doc.embed_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 sm:flex-none bg-primary-500 hover:bg-primary-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap text-center"
                        >
                          Open
                        </a>
                      ) : (
                        <button
                          onClick={() => handleDownload(doc.id, doc.file_name)}
                          className="flex-1 sm:flex-none bg-primary-500 hover:bg-primary-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          Download
                        </button>
                      )}
                      <button
                        onClick={() => togglePin(doc.id, doc.is_pinned, doc.title)}
                        className={`flex-1 sm:flex-none px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                          doc.is_pinned
                            ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {doc.is_pinned ? 'Unpin' : 'Pin'}
                      </button>
                      <button
                        onClick={() => handleDelete(doc.id, doc.title)}
                        className="flex-1 sm:flex-none bg-danger-500 hover:bg-danger-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => {
                          setDocumentToMove(doc);
                          setShowMoveDocumentModal(true);
                        }}
                        className="flex-1 sm:flex-none bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap"
                      >
                        Move...
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
          </div>
        </div>
      )}

      {/* Add Document Form */}
      {view === 'upload' && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          {/* Upload Mode Toggle */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              type="button"
              onClick={() => setUploadMode('file')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                uploadMode === 'file'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Upload File
            </button>
            <button
              type="button"
              onClick={() => setUploadMode('embed')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                uploadMode === 'embed'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Link External Document
            </button>
          </div>

          {/* File Upload Form */}
          {uploadMode === 'file' && (
          <form onSubmit={handleUpload} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select File *
              </label>
              <input
                id="file-upload"
                type="file"
                onChange={handleFileSelect}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Supported formats: PDF, Word, Excel, text files, images (max 10MB)
              </p>
              {uploadForm.file && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm text-green-800">
                    Selected: {uploadForm.file.name} ({(uploadForm.file.size / (1024 * 1024)).toFixed(2)} MB)
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Title *
                </label>
                <input
                  type="text"
                  required
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter document title..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                  placeholder="Optional description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="general">General</option>
                  <option value="policy">Policy</option>
                  <option value="training">Training</option>
                  <option value="forms">Forms</option>
                  <option value="resources">Resources</option>
                  <option value="guidelines">Guidelines</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
                <select
                  value={uploadForm.visibility}
                  onChange={(e) => setUploadForm({...uploadForm, visibility: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Users</option>
                  <option value="volunteers_only">Volunteers Only</option>
                  <option value="admin_only">Admins Only</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Folder (Optional)</label>
                <select
                  value={uploadForm.folder_id || ''}
                  onChange={(e) => setUploadForm({...uploadForm, folder_id: e.target.value || null})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Root (No folder)</option>
                  {folders.map(folder => (
                    <option key={folder.id} value={folder.id}>{folder.name}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={uploadForm.is_pinned}
                    onChange={(e) => setUploadForm({...uploadForm, is_pinned: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Pin this document (appears at top)</span>
                </label>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 sm:pt-6 border-t">
              <button
                type="button"
                onClick={() => { resetUploadForm(); setView('list'); }}
                className="w-full sm:w-auto bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="w-full sm:w-auto bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {uploading ? 'Uploading...' : 'Upload Document'}
              </button>
            </div>
          </form>
          )}

          {/* Embed OneDrive Form */}
          {uploadMode === 'embed' && (
          <form onSubmit={handleEmbed} className="space-y-6">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <p className="text-sm text-indigo-800">
                Link a OneDrive, SharePoint, Google Docs/Sheets, or Canva document by pasting its share URL. Volunteers will be able to view and edit the document directly within ComitySpace.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Title *
                </label>
                <input
                  type="text"
                  required
                  value={embedForm.title}
                  onChange={(e) => setEmbedForm({...embedForm, title: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter document title..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document URL *
                </label>
                <input
                  type="url"
                  required
                  value={embedForm.embed_url}
                  onChange={(e) => setEmbedForm({...embedForm, embed_url: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="https://docs.google.com/... or https://yourorg.sharepoint.com/..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Paste the share link from OneDrive, SharePoint, Google Docs/Sheets, or Canva
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Password
                </label>
                <input
                  type="text"
                  value={embedForm.embed_password}
                  onChange={(e) => setEmbedForm({...embedForm, embed_password: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter password if document is password-protected"
                />
                <p className="text-xs text-gray-500 mt-1">
                  If the share link is password-protected, enter the password here so volunteers can access it
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={embedForm.description}
                  onChange={(e) => setEmbedForm({...embedForm, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                  placeholder="Optional description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={embedForm.category}
                  onChange={(e) => setEmbedForm({...embedForm, category: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="general">General</option>
                  <option value="policy">Policy</option>
                  <option value="training">Training</option>
                  <option value="forms">Forms</option>
                  <option value="resources">Resources</option>
                  <option value="guidelines">Guidelines</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
                <select
                  value={embedForm.visibility}
                  onChange={(e) => setEmbedForm({...embedForm, visibility: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Users</option>
                  <option value="volunteers_only">Volunteers Only</option>
                  <option value="admin_only">Admins Only</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Folder (Optional)</label>
                <select
                  value={embedForm.folder_id || ''}
                  onChange={(e) => setEmbedForm({...embedForm, folder_id: e.target.value || null})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Root (No folder)</option>
                  {folders.map(folder => (
                    <option key={folder.id} value={folder.id}>{folder.name}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={embedForm.is_pinned}
                    onChange={(e) => setEmbedForm({...embedForm, is_pinned: e.target.checked})}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Pin this document (appears at top)</span>
                </label>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 sm:pt-6 border-t">
              <button
                type="button"
                onClick={() => { resetEmbedForm(); setView('list'); }}
                className="w-full sm:w-auto bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="w-full sm:w-auto bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {uploading ? 'Linking...' : 'Link Document'}
              </button>
            </div>
          </form>
          )}
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Create New Folder</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Folder Name *
                </label>
                <input
                  type="text"
                  value={folderForm.name}
                  onChange={(e) => setFolderForm({...folderForm, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter folder name..."
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={folderForm.description}
                  onChange={(e) => setFolderForm({...folderForm, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                  placeholder="Optional description..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateFolderModal(false);
                  resetFolderForm();
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createFolder}
                disabled={!folderForm.name.trim()}
                className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:bg-primary-300 disabled:cursor-not-allowed transition-colors"
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {showRenameFolderModal && folderToEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Rename Folder</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Folder Name *
                </label>
                <input
                  type="text"
                  value={folderForm.name}
                  onChange={(e) => setFolderForm({...folderForm, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter folder name..."
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={folderForm.description}
                  onChange={(e) => setFolderForm({...folderForm, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                  placeholder="Optional description..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowRenameFolderModal(false);
                  setFolderToEdit(null);
                  resetFolderForm();
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={renameFolder}
                disabled={!folderForm.name.trim()}
                className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:bg-primary-300 disabled:cursor-not-allowed transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Document Modal */}
      {showMoveDocumentModal && documentToMove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Move Document</h3>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Moving: <span className="font-medium text-gray-900">{documentToMove.title}</span>
              </p>
              <p className="text-xs text-gray-500">
                Current location: {documentToMove.folder_id ? documentToMove.folder_name || 'Unnamed folder' : 'Root (No folder)'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Move to:
              </label>
              <select
                defaultValue={documentToMove.folder_id || ''}
                onChange={(e) => {
                  const targetFolderId = e.target.value ? parseInt(e.target.value) : null;
                  moveDocument(documentToMove.id, targetFolderId);
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Root (No folder)</option>
                {folders.map(folder => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowMoveDocumentModal(false);
                  setDocumentToMove(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDocumentManager;